import { Col, Row } from 'antd';
import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParallax } from 'react-scroll-parallax';
import { Tweet } from 'react-twitter-widgets';
import styled from 'styled-components';

import EirikImage from '../assets/images/eirik.png';
import Climbing from '../assets/images/LandingPage/climbing_header.png';
import EllenImage from '../assets/images/LandingPage/ellen.jpg';
import EskildImage from '../assets/images/LandingPage/eskild.jpeg';
import FrikkImage from '../assets/images/LandingPage/frikk.png';
import whatDrivesUs from '../assets/images/LandingPage/isteImg.jpg';
import JensImage from '../assets/images/LandingPage/jens.png';
import LexiImage from '../assets/images/LandingPage/lexi.png';
import NunoImage from '../assets/images/LandingPage/nuno.jpeg';
import PranavImage from '../assets/images/LandingPage/pranav.jpg';
import SophImage from '../assets/images/LandingPage/soph.jpeg';
import theme from '../assets/theme';
import HiringBanner from '../components/_atoms/HiringBanner';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import { useIsDesktop } from '../hooks/useIsDesktop';

const ManyStudents = () => {
  const { ref } = useParallax<HTMLDivElement>({
    opacity: [0.2, 1.8],
  });
  const { ref: ref1 } = useParallax<HTMLDivElement>({
    opacity: [0.2, 1.8],
  });
  const { t } = useTranslation('Host');
  return (
    <ManyStudentsContainer>
      <h1 style={{ width: '100%', textAlign: 'center' }} ref={ref}>
        + 4,000,000 {t('students')}{' '}
      </h1>

      <h2 style={{ width: '100%', textAlign: 'center' }} ref={ref1}>
        {t('have shared their voices')}
      </h2>
    </ManyStudentsContainer>
  );
};

const TwitterGrid = () => {
  return (
    <WidthContainer>
      <Row gutter={[10, 10]} style={{ marginTop: 0, marginBottom: 80, width: '100%' }}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Tweet tweetId="1689813769029820416" />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Tweet tweetId="1686180615077433346" />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Tweet tweetId="1689756295920746496" />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Tweet tweetId="1689768899376537601" />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Tweet tweetId="1686845883806056448" />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Tweet tweetId="1689992962279710720" />
        </Col>
      </Row>
    </WidthContainer>
  );
};

const ManyStudentsContainer = styled.div`
  padding: 20px;
  min-height: 400px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  flex-wrap: wrap;

  h1 {
    font-size: 48px;
    margin: 0;
    color: ${theme.colors.white};
  }
  h2 {
    font-size: 24px;
    color: ${theme.colors.white};
  }
  @media (max-width: 900px) {
    h1 {
      font-size: 40px;
      margin: 0;
    }
    h2 {
      font-size: 20px;
    }
  }
`;

const About: NextPageWithLayout = () => {
  const isLargerThanMd = useIsDesktop();

  return (
    <TopContainer>
      <FullWidth>
        <Section
          style={{
            padding: isLargerThanMd ? '50px 80px' : '50px 20px',
            marginBottom: 0,
            marginTop: 70,
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: 800,
            backgroundColor: theme.colors.green,
            borderRadius: 5,
            boxShadow: theme.effects.floatingCreatorElementShadow,
          }}
        >
          <ColContainer>
            <h2>Mission</h2>
            <p style={{ fontWeight: 'bold', fontSize: 22 }}>
              We are on a mission to help teachers spark curiosity, discussion and
              critical thinking in the classroom.
            </p>
            <p style={{ fontWeight: 'bold', fontSize: 22 }}>
              We want to help create learning experiences which excite the students, and
              where the neighbouring classrooms can hear the buzz from students
              discussing. Because we believe students will learn more, keep the joy for
              learning, and be better prepared for a world which is changing more rapidly
              than ever.
            </p>
          </ColContainer>
        </Section>
      </FullWidth>
      <ManyStudents />
      <h2>The team</h2>
      <WidthContainer>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileImage
                src={JensImage}
                alt="Co-founder & CEO"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Jens Aarre Seip</p>
                <p>Co-founder & CEO</p>
                <EmailText>jens@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileText>
                I believe curiosity is the most important prerequisite for learning. At
                17, I dropped out of high school, and it was a bumpy road finding back my
                own curiosity. Fast forward to 2019, I was in Singapore studying for my
                master’s degree, and doing a project on education in collaboration with
                McKinsey & Company. Insights from this project, my own school background,
                and long passionate discussions with Eirik and Frikk about how we believe
                education should be, sparked the idea of Curipod.
              </ProfileText>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 0 : 1}>
            <ColContainer>
              <ProfileText>
                I learn something new everyday, and it would never happen if it wasn’t for
                the inner motivation I found while studying. A couple of years later,
                having experience from building software and products, I’m still in
                pursuit of learning things in a quick and ever lasting way.
              </ProfileText>
            </ColContainer>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 1 : 0}>
            <ColContainer>
              <ProfileImage
                src={FrikkImage}
                alt="Co-founder & CTO"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Frikk Fossan</p>
                <p>Co-founder & CTO</p>
                <EmailText>frikk@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileImage
                src={EirikImage}
                alt="Academic lead"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Eirik Hernes Berre</p>
                <p>Co-Founder & COO</p>
                <EmailText>eirik@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>

          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileText>
                {`Hey there, I'm Eirik, and education is my life's passion. I was born with
                a Hermione reflex, if someone asks question my hand shoots off into the
                air. I loved school, and I’m a big geek! I have worked as an educator in
                both Norway and Mozambique, and I love teaching. The best side of teaching
                is that you get to learn everything twice. As a co-founder of Curipod, I'm
                on a mission to make students curious and give teachers more time with
                their students and families. I believe that education is the key to
                unlocking a better future for all, and I'm committed to making that a
                reality. Let's change the world together! When I'm not changing the world
                you will usually find me reading Harry Potter, LARPing or other nerdy
                activities, or just running.`}
              </ProfileText>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 0 : 1}>
            <ColContainer>
              <ProfileText>
                {`I love learning new things and the discussions that follow from new insights. During my studies at NTNU, I worked as a part-time teacher for over 5 years and have firsthand experience of the struggles in the classroom. With my master's in AI technology and previous experience in building great products, I know that I want to combine my passion for education and technology to help spark curiosity in the classroom. Curipod is the perfect place for me, as I get to build a product that teachers thrive to use while working with the latest AI technology.`}
              </ProfileText>
            </ColContainer>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 1 : 0}>
            <ColContainer>
              <ProfileImage
                src={EskildImage}
                alt="Employee with his cat named Lusk"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Eskild Ruud Mageli /w Lusk (cat-chief)</p>
                <p>Senior Software and AI engineer</p>
                <EmailText>eskild@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileImage
                src={EllenImage}
                alt="Employee"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Ellen Barkost</p>
                <p>Product Manager</p>
                <EmailText>ellen@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>

          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileText>
                {`I love new experiences, acquiring new skills and perspectives. However, as a young student, my discipline and drive to deliver results, hindered my curiosity to explore something out of interest. Eventually, after pursuing studies in Psychology, Design and Entrepreneurship, I re-discovered my curious spark solving real problems for people. 
With Curipod, I hope to help every student get in touch with their curious side, to make learning a meaningful and fun part of life. When I’m not talking to educators or pondering new solutions, you’ll find me in the ski tracks, or ticking off my growing list of Oslo experiences.`}
              </ProfileText>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 0 : 1}>
            <ColContainer>
              <ProfileText>
                {`I have a pretty diverse background, having studied Zoology at university, I've researched animals around the world. I loved school and with a mix of dyslexia and ADHD I've always had to be creative with learning. The skills I gained were put to good use when I worked as a private tutor. So how did I become a software engineer? I was introduced to the joy of coding at a startup summer school in Japan and have never looked back. I love that I can combine all my experiences and join Curipod helping students learn to love learning.`}
              </ProfileText>
            </ColContainer>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 1 : 0}>
            <ColContainer>
              <ProfileImage
                src={SophImage}
                alt="Employee with a deer in Japan"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Sophie Haugland Pedersen</p>
                <p>Software Engineer</p>
                <EmailText>sophie@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileImage
                src={LexiImage}
                alt="Employee"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Lexi Lobdell</p>
                <p>Operations Manager</p>
                <EmailText>lexi@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>

          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileText>
                {`I love learning, exploring, and trying new things in all aspects of life! This means I've moved to Oslo from sunny California, studied neuroscience and business, and have hobbies ranging from ice swimming to skydiving. My first jobs were teaching horseback riding and swimming lessons where I loved working with kids and seeing their interests grow. I'm most motivated by impact and learning, so at Curipod I love working to build a tool that allows every kid to explore their interests, voice their opinions in the classroom, and most importantly enjoy learning! When I'm not at the office, you'll likely find me outside on a trail or in some body of water.`}
              </ProfileText>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 0 : 1}>
            <ColContainer>
              <ProfileText>
                {`Tech and entrepreneurship are my ultimate obsessions — I could geek out on them for hours! There's nothing quite like the exhilarating rush of taking an idea from zero to one and crafting something that resonates with people. When I'm not knee-deep in my geeky universe, you'll catch me jet-setting to explore new cultures, indulging in sitcom marathons, or hitting the field for some sports action. With a firm conviction that education is the cornerstone of our lives, I'm thrilled to dive in and make a meaningful splash in the worlds of both students and teachers alike!`}
              </ProfileText>
            </ColContainer>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} order={isLargerThanMd ? 1 : 0}>
            <ColContainer>
              <ProfileImage
                src={PranavImage}
                alt="Employee"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Pranav Deshpande</p>
                <p>Software Engineer</p>
                <EmailText>pranav@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>
        </Row>
        <Row gutter={[36, 70]} style={{ marginBottom: 50 }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileImage
                src={NunoImage}
                alt="Employee"
                width={'300px'}
                height={'300px'}
              />
              <ProfileInfo>
                <p>Nuno Mota</p>
                <p>Software Engineer</p>
                <EmailText>nuno@curipod.com</EmailText>
              </ProfileInfo>
            </ColContainer>
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <ProfileText>
                {`I'm a retired academic and a serial entrepreneur. I started working in Tech over 15 years ago, always hopping from country to country (Portugal, Switzerland, Germany, UK, US, Norway, ...). I believe education is the key to a better future - and my background in AI and Machine Learning tells me there are lots of awesome opportunities here!`}
              </ProfileText>
            </ColContainer>
          </Col>
        </Row>
      </WidthContainer>

      <WidthContainer>
        <Row
          style={{
            marginBottom: 100,
            borderRadius: 5,
            padding: 25,
          }}
        >
          <Col sm={24} md={24} lg={24} xl={24}>
            <ColContainer>
              <h2 style={{ textAlign: 'left', width: '100%' }}>Our values</h2>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 25,
                  marginBottom: 10,
                  color: '#474747',
                }}
              >{`We build with Curiosity`}</p>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: 0,
                  color: '#828282',
                }}
              >
                {`Curious people drive the world forward, they question established truths, they learn fast, and they are incredibly fun to work with. We always approach challenges and meet new people with curiosity.`}
              </p>
              <div
                style={{
                  width: '100%',
                  height: 1,
                  background: theme.colors.divider,
                  margin: 20,
                }}
              ></div>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 25,
                  marginBottom: 10,
                  color: '#474747',
                }}
              >{`We build with empathy`}</p>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: 10,
                  color: '#828282',
                }}
              >
                {`We want every student to participate, and we don’t settle if 29 out 30 do. To build something which resonates with every student is really hard, and we believe that without empathy we will never get there. Also, we really like to be around nice people 😊`}
              </p>
            </ColContainer>
          </Col>
        </Row>
      </WidthContainer>
      <Section>
        <div
          style={{
            width: '100%',
            paddingTop: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <HiringBanner />
        </div>
      </Section>

      <TwitterGrid />

      <WidthContainer>
        <Row
          style={{
            marginBottom: 100,
            background: theme.colors.white,
            borderRadius: 5,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            padding: 25,
          }}
        >
          <Col sm={24} md={24} lg={24} xl={24}>
            <ColContainer>
              <h2 style={{ textAlign: 'left', width: '100%' }}>Strong backing</h2>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 25,
                  marginBottom: 0,
                  color: '#474747',
                }}
              >{`Reach Capital`}</p>
              <div
                style={{
                  width: '100%',
                  height: 1,
                  background: theme.colors.divider,
                  margin: 20,
                }}
              ></div>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 25,
                  marginBottom: 0,
                  color: '#474747',
                }}
              >{`Startuplab`}</p>
              <div
                style={{
                  width: '100%',
                  height: 1,
                  background: theme.colors.divider,
                  margin: 20,
                }}
              ></div>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 25,
                  marginBottom: 0,
                  color: '#474747',
                }}
              >{`Sondo Capital`}</p>
              <div
                style={{
                  width: '100%',
                  height: 1,
                  background: theme.colors.divider,
                  margin: 20,
                }}
              ></div>
              <p
                style={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: 25,
                  color: '#474747',
                }}
              >{`Sagene Tech Ventures`}</p>
            </ColContainer>
          </Col>
        </Row>
      </WidthContainer>

      <WidthContainer>
        <Row style={{ marginBottom: 100 }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            <ColContainer>
              <h2>What drives us</h2>
              <p>
                {`We believe the best learning happens when you learn because you are deeply motivated to learn and grow. Our way of helping you find this motivation is by helping you ask questions and discuss with others.`}
              </p>
              <p>
                {`The most impact we can do is by building technology that connects learners and enable teachers to spend less time planning and more time helping learners be more curious together.`}
              </p>
            </ColContainer>
          </Col>
          <Col sm={24} md={24} lg={12} xl={12} style={{ padding: '0px 18px' }}>
            <ColContainer>
              <ProfileImage src={whatDrivesUs} alt="COO" />
            </ColContainer>
          </Col>
        </Row>
      </WidthContainer>
      <Section>
        <h2>Educational technology made in Norway</h2>
        <p>
          We are a remote distributed team based in Norway. Being a part of Scandinavia’s
          thriving edtech culture, we bring the latest development of edtech inspired by
          Scandianvian pedagogy.
        </p>
        <Link href="/learning-principles/">
          <a>Our learning principles</a>
        </Link>
      </Section>
    </TopContainer>
  );
};

const WidthContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  width: 900px;
  position: relative;
  @media ${theme.device.laptop} {
    width: 600px;
  }
  @media ${theme.device.tablet} {
    width: 400px;
  }
  @media ${theme.device.mobileL} {
    width: 300px;
  }
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0px;

  h1 {
    font-size: 60px;
    color: rgb(71, 71, 71);
  }

  h2 {
    font-size: 40px;
    margin-bottom: 50px;
    color: rgb(71, 71, 71);
  }

  p,
  a {
    font-size: 18px;
    line-height: 30px;
    color: #828282;
  }

  a {
    font-weight: bold;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const Section = styled(WidthContainer)`
  margin-bottom: 100px;
`;

const ProfileImage = styled(Image)`
  max-height: 300px;
  max-width: 300px;
  border-radius: 20px;
  object-fit: cover;
`;

const ProfileInfo = styled.div`
  margin-top: 5px;
  p {
    margin-bottom: 5px;
    font-size: 14px;
    font-weight: bold;
    line-height: 14px;
  }
`;
const ProfileText = styled.p`
  margin-bottom: 0;
`;

const ColContainer = styled.div`
  height: 100%;
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const EmailText = styled.p`
  font-weight: lighter;
  font-size: 0.9rem;
`;

const FullWidth = styled.div`
  position: relative;
  padding: 30px 0;
  width: 100%;
  h1 {
    color: white;
  }
  h2 {
    color: white;
  }
  p {
    color: white;
  }
`;

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: { message: 'Next.js is awesome' }, // will be passed to the page component as props
  };
};
About.getLayout = function getLayout(page: ReactElement) {
  return (
    <LandingPageLayout
      title="Spark curiosity, discussion and critical thinking in the classroom"
      subTitle="For too long, education has been focusing on remembering, recalling and
      memorising."
      image={Climbing}
      hideActions={true}
    >
      {page}
    </LandingPageLayout>
  );
};
export default About;
